import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAddDrive, useAvailableDrives } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type AddDriveDialogProps = {
  memberId: number;
  sourceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AddDriveDialog({ memberId, sourceId, open, onOpenChange }: AddDriveDialogProps) {
  const available = useAvailableDrives(memberId, open);
  const addDrive = useAddDrive();
  const [selectedId, setSelectedId] = useState<string>('');
  const drives = (available.data?.drives ?? []).filter((drive) => drive.sourceId === sourceId);

  const handleAdd = () => {
    if (!selectedId) {
      return;
    }
    addDrive.mutate(
      { providerDriveId: selectedId, sourceId },
      {
        onSuccess: (drive) => {
          toast.success(`Added ${drive.name}.`);
          setSelectedId('');
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to add the drive.'));
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add drive</DialogTitle>
          <DialogDescription>
            Drives this source exposes. Files under the roots you configure will be referenced read-only.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {available.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading drives...</p>
          ) : null}
          {available.isError ? (
            <p className="text-sm text-destructive">
              {getAxiosErrorDisplayMessage(available.error, 'Failed to list available drives.')}
            </p>
          ) : null}
          {!available.isLoading && !available.isError && drives.length === 0 ? (
            <p className="text-sm text-muted-foreground">No drives are available from this source yet.</p>
          ) : null}
          {drives.length > 0 ? (
            <RadioGroup value={selectedId} onValueChange={setSelectedId}>
              {drives.map((drive) => (
                <div key={drive.providerDriveId} className="flex items-start space-x-2">
                  <RadioGroupItem
                    value={drive.providerDriveId}
                    id={`drive-${drive.providerDriveId}`}
                    disabled={drive.alreadyAdded}
                    className="mt-1"
                  />
                  <Label htmlFor={`drive-${drive.providerDriveId}`} className="flex flex-col">
                    <span>{`${drive.name} · ${drive.productLabel}`}</span>
                    <span className="text-xs text-muted-foreground">
                      {drive.siteName ?? 'Unknown location'}
                      {drive.alreadyAdded ? ' · already added' : ''}
                    </span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleAdd} disabled={!selectedId || addDrive.isPending}>
            {addDrive.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add drive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
